<template>
  <div class="privacy-main">
    <h1 class="main-title">{{appName}} User Privacy Policy</h1>
    <div class="content-wrap">
      <h2 class="sub-title">Data Protection Statement</h2>
      <p>
        When you utilize our mobile application services, we will collect and process your personal information.
        This statement elaborates on how our platform gathers, uses and discloses user data during your service usage.
      </p>
      <br>
      <p class="remind-info">
        Please read this document carefully to learn about our rules for protecting your personal information.
      </p>
      <br>

      <p class="dir-title">Document Outline</p>
      <p>1. Collection and usage of personal information</p>
      <p>2. Sharing, transmission and public disclosure of personal data</p>
      <p>3. Usage of Cookies, SDK and related technical tools</p>
      <p>4. Security protection measures for user data</p>
      <p>5. Personal information management methods</p>
      <p>6. Data processing rules for teenage users</p>
      <p>7. Storage rules of personal information</p>
      <p>8. Policy update and notification rules</p>
      <p>9. Contact method for inquiries</p>
      <br>

      <h3 class="chapter">Overview</h3>
      <p>
        {{appName}} takes user information security very seriously. We keep all collected user data strictly confidential
        and have built a complete protection system to provide safe and reliable services for every user.
      </p>
      <br>

      <h3 class="chapter">1. Collection & Usage of Personal Information</h3>
      <p>
        Personal information refers to data recorded by electronic means that can identify an individual or reflect personal activities,
        including name, birthday, biometric data, contact address, chat records, account passwords, property details,
        health status and transaction information.
      </p>
      <br>
      <p>
        Sensitive personal information means data that may cause risks to personal and property safety, reputation damage
        or unfair treatment once leaked or misused. It includes biometrics, bank accounts, chat content, location data,
        health records and information of users under the age of 14.
      </p>
      <br>

      <p class="strong-txt">You acknowledge and agree to the following terms:</p>
      <p>
        1. We offer various services to meet your different needs. Different functions will collect different types of personal data,
        which shall comply with the rules of corresponding service modules.
      </p>
      <p>
        2. We continuously upgrade our products and technologies. When launching new features, we may collect new data
        or adjust existing usage rules. We will inform you via policy updates or pop-up prompts, and collect data only after you agree.
        You can contact us anytime if you have questions.
      </p>
      <p>
        3. Data collected from one function can be used for other platform services, such as personalized content recommendation
        and security reminders. If we use your data beyond the agreed scope, we will obtain your approval again in advance.
      </p>
      <br>

      <p class="note-txt">Special Reminder:</p>
      <p>
        Our platform provides user recommendation services. Your nickname, age, gender, star sign, occupation, hometown,
        personal signature, avatar, photos and public posts may be displayed and recommended to other users.
      </p>
      <br>

      <h3 class="chapter">Business Functions & Collected Data</h3>
      <p>
        As a social communication application, our core service is to provide online text, picture, voice and video instant messaging.
        The information collected by each function is as follows:
      </p>
      <br>
      <p>
        1. Account Registration: You need to provide email, nickname, gender, country, birthday and avatar to complete registration.
        We also collect device information including device model, system version, device ID, network status and application list
        to ensure the stable operation of the application.
      </p>
      <p>
        2. Chat Service: Basic chat will not ask for extra information. We collect your account data to guarantee service stability and account security.
        Sending pictures, voice or video calls requires album and microphone permissions. Refusing these permissions will not affect basic chat.
      </p>
      <p>
        3. Video Call: This function requires camera and microphone permissions. You cannot use video call without relevant authorization.
      </p>
      <p>
        4. Moments Browsing: No personal data or extra permissions are required to view moments content.
      </p>
      <p>
        5. User Homepage: Viewing others' albums and videos needs no special permissions. Video call on homepage requires camera and microphone access.
      </p>
      <p>
        6. Profile Editing: Changing avatar needs album permission, and refusing it will not affect basic use.
      </p>
      <p>
        7. Payment Service: The payment function is provided by third-party payment institutions. We cannot obtain your payment information.
        Disabling related permissions only affects payment, other functions can be used normally.
      </p>
      <p>
        8. Message Notification: We apply for auto-start permission to push message reminders. You can turn off this permission in mobile settings.
      </p>
      <br>

      <p>
        Camera, album and microphone permissions are disabled by default. We will pop up authorization windows when you use related functions.
        You can turn permissions on or off freely. Closing permissions means you withdraw authorization, and we will stop collecting corresponding data.
        This will not affect the data collected before you cancel authorization.
      </p>
      <br>

      <p>
        We may send service notifications, security verification and user research messages to your reserved email.
        If you publish other people's information on the platform, you must obtain legal authorization in advance.
      </p>
      <br>

      <p>
        We will use your information during your service period. After you cancel your account or stop using certain functions,
        we will stop using the data and perform anonymization or deletion in accordance with laws.
      </p>
      <br>

      <h3 class="chapter">Rules for Using Collected Data</h3>
      <p>
        We only use your data for the purposes stated in this policy. Any other usage will get your prior consent.
        When obtaining data from third parties, we will verify the legality of data sources and adopt the same high-standard security protection.
      </p>
      <br>

      <p class="strong-txt">Authorization Exceptions</p>
      <p>
        In the following situations, we do not need your consent to collect and use personal information,
        and may not handle your requests for modification, deletion or withdrawal of consent:
      </p>
      <p>1. To perform legal obligations stipulated by laws and regulations</p>
      <p>2. Matters related to national security and national defense</p>
      <p>3. For public safety, public health and public interests</p>
      <p>4. For criminal investigation, litigation, trial and judgment execution</p>
      <p>5. To protect legitimate rights of individuals when consent cannot be obtained</p>
      <p>6. Information that you actively disclose to the public</p>
      <p>7. Data collected from legally public information sources</p>
      <br>

      <p>
        We will conduct desensitization, statistical analysis and algorithm training on collected data to optimize products.
        Anonymous data cannot identify specific individuals, so no extra notification or consent is needed.
        If we stop platform operation, we will stop data collection, issue notices and delete or anonymize all stored data.
      </p>
      <br>

      <h3 class="chapter">Information You Share</h3>
      <p>
        You can share texts and pictures through our platform, which may contain personal or sensitive data.
        Please be careful when publishing private content. You can delete your published content,
        but it may still be saved by other users or third parties beyond our control.
      </p>
      <br>

      <h3 class="chapter">2. Sharing, Transfer & Public Disclosure</h3>
      <h4 class="sub-chap">Data Sharing</h4>
      <p>
        We will not share your personal information with external parties except the following circumstances:
      </p>
      <p>1. Share data after getting your explicit consent;</p>
      <p>2. Share data as required by laws and judicial authorities;</p>
      <p>3. Share necessary data with affiliated companies for joint services. We will ask for your consent again for sensitive data;</p>
      <p>4. Provide necessary data to authorized partners who can only use data for assigned work.</p>
      <br>

      <h4 class="sub-chap">Data Transfer</h4>
      <p>
        We will not transfer your personal information unless:
      </p>
      <p>1. You have given explicit consent for data transfer;</p>
      <p>2. In case of merger, acquisition or bankruptcy, the new holder must abide by this policy, otherwise re-authorization is required.</p>
      <br>

      <h4 class="sub-chap">Public Disclosure</h4>
      <p>1. Disclose information with your active consent or operation;</p>
      <p>2. Disclose information according to rules to protect public interests when you violate relevant regulations.</p>
      <br>

      <h3 class="chapter">3. Usage of Cookies and SDK</h3>
      <h4 class="sub-chap">Cookies</h4>
      <p>
        We use Cookies to ensure normal service operation, optimize use experience and recommend interested content.
        You can clear Cookies in browser settings, while some dependent functions may become unavailable after clearing.
      </p>
      <br>

      <h4 class="sub-chap">Third-Party SDK</h4>
      <p>
        Partial functions rely on third-party SDKs, including Google Firebase Push SDK, Third-party Payment SDK,
        NetEase Yunxin SDK, Shengwang SDK, AppsFlyer SDK, Facebook SDK and Huawei Push SDK.
        Refusing related permissions only disables corresponding third-party functions, and will not affect other normal services.
      </p>
      <br>

      <h3 class="chapter">4. Personal Information Security Protection</h3>
      <p>
        We follow relevant laws and adopt multiple security measures to protect your data:
      </p>
      <p>
        1. We use encryption, desensitization and access control to prevent unauthorized access, leakage and damage of data,
        and arrange regular security training for staff.
      </p>
      <p>
        2. We have a complete data security management system. Once a data leakage accident occurs, we will start emergency plans and inform users in time.
      </p>
      <p>
        3. We only collect necessary data and retain information within the required time limit unless extended by law.
      </p>
      <p>
        4. The network environment is not absolutely safe. Do not send sensitive information through unofficial channels.
        Please contact customer service immediately if your account or password is leaked.
      </p>
      <br>

      <h3 class="chapter">5. Manage Your Personal Information</h3>
      <h4 class="sub-chap">Information Query</h4>
      <p>
        You have the right to view your personal information within legal scope.
        You can check and edit information via <strong>[Mine - Settings]</strong>.
        Enter personal center and click avatar to modify nickname, avatar, birthday and personal introduction.
      </p>
      <br>

      <h4 class="sub-chap">Information Correction</h4>
      <p>
        You can apply to correct wrong personal information through the above entry.
      </p>
      <br>

      <h4 class="sub-chap">Information Deletion</h4>
      <p>
        You can delete part of your personal information independently. You can also apply for deletion in the following cases:
      </p>
      <p>1. Our data processing violates relevant laws;</p>
      <p>2. Data is collected without your consent;</p>
      <p>3. We seriously violate the agreed agreement;</p>
      <p>4. You stop using services or cancel your account;</p>
      <p>5. We permanently stop providing services for you.</p>
      <br>

      <h4 class="sub-chap">Adjust Authorization</h4>
      <p>
        You can enable or cancel data collection permissions by yourself, or contact customer service for help.
      </p>
      <br>

      <h3 class="chapter">6. Protection for Minors</h3>
      <p class="forbid-text">Users under 18 years old are not allowed to register and use this application.</p>
      <p>
        Our services are not oriented to minors. We will not actively collect or sell minors' personal information.
        If we accidentally obtain minor users' data, we will delete it at once.
        We will obtain guardian's verified consent before collecting minors' information if we adjust relevant rules in the future.
      </p>
      <br>

      <h3 class="chapter">7. Data Storage Rules</h3>
      <p>
        Our servers are deployed around the world. Your personal information may be stored and used on overseas servers.
        You agree to cross-regional transmission and storage of your data for the purposes stated in this policy.
      </p>
      <br>

      <h3 class="chapter">8. Policy Update Notice</h3>
      <p>
        We have the right to revise this privacy policy. You can check the latest version on our official website.
        We will release announcements for major changes. Your continued use of services means you accept the updated policy.
        Stop using our services if you disagree with the new terms.
      </p>
      <br>

      <h3 class="chapter">9. Contact Us</h3>
      <p>
        If you have any questions about this policy or data protection issues, please send email to:
        <span class="email">{{appName}}@{{appName}}.site</span>
      </p>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {}
  },
  created() {}
}
</script>

<style scoped>
/* 全新移动端清新风格，独立样式 */
.privacy-main {
  width: 100%;
  padding: 16px;
  box-sizing: border-box;
  background-color: #f5f8ff;
  min-height: 100vh;
  font-family: sans-serif;
}

.main-title {
  font-size: 22px;
  color: #27374d;
  text-align: center;
  margin: 0 0 18px 0;
  font-weight: 700;
}

.content-wrap {
  width: 100%;
  max-width: 750px;
  margin: 0 auto;
  background: #ffffff;
  padding: 24px 20px;
  border-radius: 8px;
  box-shadow: 0 1px 8px rgba(39, 55, 77, 0.1);
  font-size: 14px;
  line-height: 1.75;
  color: #526d82;
}

.sub-title {
  font-size: 17px;
  color: #277da1;
  text-align: center;
  margin: 0 0 14px 0;
  font-weight: 600;
}

.remind-info {
  background: #f0f7ff;
  color: #277da1;
  padding: 10px;
  border-radius: 6px;
}

.dir-title {
  font-size: 15px;
  color: #27374d;
  font-weight: 600;
  margin: 8px 0;
}

.chapter {
  font-size: 16px;
  color: #277da1;
  font-weight: 600;
  margin: 24px 0 10px 0;
}

.sub-chap {
  font-size: 15px;
  color: #334155;
  font-weight: 600;
  margin: 12px 0 6px 0;
}

.strong-txt {
  font-weight: 600;
  color: #e63946;
}

.note-txt {
  font-weight: 600;
  color: #e63946;
}

.forbid-text {
  font-size: 15px;
  font-weight: 600;
  color: #e63946;
}

.email {
  color: #277da1;
  font-weight: 500;
}
</style>